/**
 * UndoToast — Things 3-style undo toast using Sonner.
 *
 * Design decisions (§28 + POC 38):
 * - Dark chip style, positioned bottom-center
 * - 5s auto-dismiss (Sonner default), "Undo" action button
 * - Max 1 visible at a time (Sonner handles via dismiss)
 * - Cmd+Z also undoes → toast disappears
 * - Description comes from the operation's registerUndo() call
 */

import i18n from "i18next"
import { toast } from "sonner"
import { useUndoStore } from "./undoStore"
import { translateUndoDescription } from "./undoDescription"

/** Translate an operation's undo description (an English literal) for the toast. */
function translateDescription(description: string): string {
  return translateUndoDescription(description, {
    exists: (key) => i18n.exists(key),
    t: (key, options) => i18n.t(key, options),
  })
}

/** ID of the currently-visible undo toast (for dismiss on Cmd+Z) */
let activeToastId: string | number | undefined

/**
 * Show an undo toast for the most recent action.
 * The toast has an "Undo" button that triggers useUndoStore.undo().
 *
 * @param description - What happened (e.g., "Completed task", "Deleted 3 tasks")
 */
export function showUndoToast(description: string): void {
  // Dismiss previous undo toast (max 1 visible)
  if (activeToastId !== undefined) {
    toast.dismiss(activeToastId)
  }

  activeToastId = toast(translateDescription(description), {
    action: {
      label: i18n.t("Undo"),
      onClick: () => {
        void useUndoStore.getState().undo()
        activeToastId = undefined
      },
    },
    onDismiss: () => {
      activeToastId = undefined
    },
    onAutoClose: () => {
      activeToastId = undefined
    },
    duration: 5000,
  })
}

/**
 * Dismiss the active undo toast (called when Cmd+Z undoes via keyboard).
 * This prevents a stale toast with an already-consumed undo entry.
 */
export function dismissUndoToast(): void {
  if (activeToastId !== undefined) {
    toast.dismiss(activeToastId)
    activeToastId = undefined
  }
}
